/**
 * FHIR Consent-related constants for genetic testing
 */

import { Coding } from '@medplum/fhirtypes';
import { SYSTEMS, HL7, EXTERNAL_CS, ARKON } from './systems.consts';

/**
 * SNOMED CT codes for genetic test consent types
 */
export const GENETIC_TEST_CONSENT_CODES = {
  SCIENTIFIC_USE: {
    system: SYSTEMS.SNOMED,
    code: '720801000000101',
    display: 'Medical research (Scientific Research)',
  } as Coding,

  PHOTO_VIDEO: {
    system: SYSTEMS.SNOMED,
    code: '257444003',
    display: 'Photograph',
  } as Coding,

  INCIDENTAL_FINDINGS: {
    system: SYSTEMS.SNOMED,
    code: '261087003',
    display: 'Incidental findings',
  } as Coding,

  SECONDARY_FINDINGS: {
    system: SYSTEMS.SNOMED,
    code: '2603003',
    display: 'Secondary findings',
  } as Coding,
} as const;

/**
 * FHIR Consent scope and category codes
 */
export const CONSENT_SCOPE: Coding = {
  // https://hl7.org/fhir/R4/valueset-consent-scope.html#expansion
  system: HL7.TERMINOLOGY.CS.CONSENT.SCOPE.BASE,
  code: HL7.TERMINOLOGY.CS.CONSENT.SCOPE.CODES.RESEARCH,
  display: 'Research',
};

export const CONSENT_CATEGORY: Coding = {
  // https://hl7.org/fhir/R4/codesystem-consent-category.html#consent-category-research
  system: HL7.TERMINOLOGY.CS.CONSENT.CATEGORY.BASE,
  code: HL7.TERMINOLOGY.CS.CONSENT.CATEGORY.CODES.RESEARCH,
  display: 'Research Information Access',
};

export const CONSENT_POLICY_RULE: Coding = {
  system: HL7.TERMINOLOGY.CS.CONSENT.POLICY_CODES.BASE,
  code: HL7.TERMINOLOGY.CS.CONSENT.POLICY_CODES.CODES.CRIC,
  display: 'Common Rule Informed Consent',
};

/**
 * Consent provision types
 */
export const CONSENT_PROVISION_TYPES = {
  PERMIT: 'permit',
  DENY: 'deny',
} as const;

/**
 * Consent status tag codes for ServiceRequest
 */
export const CONSENT_STATUS_TAG_CODES = {
  PERMIT: {
    system: ARKON.SD.CONSENT_STATUS_TAG,
    code: CONSENT_PROVISION_TYPES.PERMIT,
  },
  DENY: {
    system: ARKON.SD.CONSENT_STATUS_TAG,
    code: CONSENT_PROVISION_TYPES.DENY,
  },
  NOT_ANSWERED: {
    system: ARKON.SD.CONSENT_STATUS_TAG,
    code: 'not-answered',
  },
} as const;

/**
 * Document type coding for consent documents
 */
export const CONSENT_DOCUMENT_CODING: Coding = {
  system: SYSTEMS.LOINC,
  code: EXTERNAL_CS.LOINC.CODES.CONSENT_DOCUMENT,
  display: 'Consent Document',
};

/**
 * Relationship code mappings for RelatedPerson resources
 * Based on HL7 v3 RoleCode vocabulary
 */
export const RELATIONSHIP_CODE_MAPPINGS = {
  MTH: {
    code: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER,
    display: 'mother',
    system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
  },
  FTH: {
    code: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER,
    display: 'father',
    system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
  },
  GUARD: {
    code: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.GUARDIAN,
    display: 'guardian',
    system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
  },
  POWATT: {
    code: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.POWER_OF_ATTORNEY,
    display: 'power of attorney',
    system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
  },
} as const;
